import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { AppDispatch, RootState } from '../redux';
import { createSupportTicket } from '../redux/support/supportSlice';

const CATEGORIES = [
  'Getting Started',
  'Account Management',
  'Billing & Payments',
  'Clinical Tools',
  'Technical Issue',
  'Other',
];

const ContactSupportPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isSending, error } = useSelector((state: RootState) => (state as any).support);

  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    category: CATEGORIES[0],
    priority: 'normal',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(
      createSupportTicket({
        subject: form.subject || `Support request from ${form.name}`,
        message: form.message,
        category: form.category,
        priority: form.priority,
      })
    );
    if (createSupportTicket.fulfilled.match(result)) setSubmitted(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-6">
          <h1 className="text-2xl font-bold text-white mb-1">Contact Support</h1>
          <p className="text-purple-200 text-sm">Our team typically responds within a few hours.</p>
        </div>

        <div className="p-8">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-green-100 rounded-full">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Ticket Submitted!</h2>
              <p className="text-gray-600 text-sm mb-4">
                Thank you for reaching out. A support ticket has been created and our team will respond shortly.
              </p>
              <button
                onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', category: CATEGORIES[0], priority: 'normal', message: '' }); }}
                className="text-purple-600 text-sm hover:underline"
              >
                Submit another request
              </button>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input id="name" name="name" type="text" value={form.name} onChange={handleChange} required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent text-sm" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input id="email" name="email" type="email" value={form.email} onChange={handleChange} required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent text-sm" />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input id="subject" name="subject" type="text" value={form.subject} onChange={handleChange} required
                  placeholder="Brief description of your issue"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent text-sm" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select id="category" name="category" value={form.category} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent text-sm bg-white">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select id="priority" name="priority" value={form.priority} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent text-sm bg-white">
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea id="message" name="message" value={form.message} onChange={handleChange} required rows={5}
                  placeholder="Describe your issue in as much detail as possible..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent text-sm resize-none" />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 px-4 py-3 rounded-xl">
                  <AlertCircle className="w-4 h-4 shrink-0" />{error}
                </div>
              )}

              <button type="submit" disabled={isSending}
                className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2">
                {isSending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : 'Send Message'}
              </button>
            </form>
          )}

          <div className="mt-6 border-t pt-5 text-sm text-gray-500 space-y-1">
            <div>Or email us at <a href="mailto:support@therapistdirect.com" className="text-purple-600 hover:underline">support@therapistdirect.com</a></div>
            <div>For urgent issues, call <a href="tel:+1234567890" className="text-indigo-600 hover:underline">(123) 456-7890</a></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSupportPage;
